/**
 * AdminEmailsTab — superuser-only view of email_send_log.
 *
 * Reads /api/admin/emails with keyset pagination + filters. Auto-refreshes
 * every 5 s while the tab is visible so a freshly-sent OTP appears almost
 * immediately for support debugging.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type EmailRow = {
  id: string;
  created_at: string;
  to_email: string;
  from_email: string;
  subject: string;
  kind: string;
  // 'accepted' = Resend returned 200 (current); 'sent' = legacy label for rows
  // written before the B12 rename — both exist in the DB, both are shown.
  // delivered/bounced/complained/suppressed are written by the Resend webhook
  // and are the only real delivery outcomes.
  status:
    | "queued"
    | "accepted"
    | "sent"
    | "delivered"
    | "bounced"
    | "complained"
    | "suppressed"
    | "failed"
    | "error";
  smtp_response: string | null;
  error: string | null;
  duration_ms: number | null;
  related_user_id: string | null;
  request_ip: string | null;
};

type EmailsResponse = {
  items: EmailRow[];
  next_cursor: string | null;
  totals: Record<EmailRow["status"], number>;
};

const KIND_OPTIONS = [
  "signup_otp",
  "passkey_reset",
  "intake_summary",
  "admin_signup_notify",
  "user_welcome",
  "admin_passkey_reset",
  "email_confirmation",
  "other",
];

const STATUS_OPTIONS = [
  "accepted",
  "delivered",
  "bounced",
  "complained",
  "suppressed",
  "sent",
  "failed",
  "error",
  "queued",
] as const;

const REFRESH_MS = 5_000;

const STATUS_LABELS: Record<EmailRow["status"], string> = {
  accepted: "Geaccepteerd door Resend (geen bezorgbevestiging)",
  sent: "Verzonden (legacy)",
  delivered: "Bezorgd (bevestigd door Resend-webhook)",
  bounced: "Gebounced door de ontvangende mailserver",
  complained: "Als spam gemarkeerd door de ontvanger",
  suppressed:
    "Onderdrukt door Resend (adres op de suppressielijst — verwijder het in het Resend-dashboard)",
  failed: "Mislukt",
  error: "Fout",
  queued: "In wachtrij",
};

function StatusBadge({ status }: { status: EmailRow["status"] }) {
  const styles: Record<EmailRow["status"], string> = {
    accepted: "bg-green-100 text-green-700",
    sent: "bg-green-50 text-green-600",
    delivered: "bg-emerald-200 text-emerald-800",
    bounced: "bg-red-100 text-red-700",
    complained: "bg-red-100 text-red-700",
    suppressed: "bg-red-200 text-red-800",
    failed: "bg-amber-100 text-amber-700",
    error: "bg-red-100 text-red-700",
    queued: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${styles[status]}`}
      title={STATUS_LABELS[status]}
    >
      {status}
    </span>
  );
}

function KindBadge({ kind }: { kind: string }) {
  return (
    <span className="rounded bg-[var(--cs-ivory)] px-1.5 py-0.5 text-[10px] font-medium text-[rgba(10,52,96,0.78)]">
      {kind}
    </span>
  );
}

export function AdminEmailsTab() {
  const [data, setData] = useState<EmailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filters
  const [toEmail, setToEmail] = useState("");
  const [kind, setKind] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [days, setDays] = useState(7);

  const buildUrl = useCallback((cursor?: string) => {
    const params = new URLSearchParams();
    params.set("limit", "100");
    if (cursor) params.set("cursor", cursor);
    if (toEmail.trim()) params.set("to_email", toEmail.trim());
    if (kind) params.set("kind", kind);
    if (status) params.set("status", status);
    if (days > 0) {
      const since = new Date(Date.now() - days * 86400_000).toISOString();
      params.set("since", since);
    }
    return `/api/admin/emails?${params.toString()}`;
  }, [toEmail, kind, status, days]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await authFetchJson<EmailsResponse>(buildUrl());
      setData(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kon e-maillog niet ophalen.");
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll every 5s while visible
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void load();
    };
    pollRef.current = window.setInterval(tick, REFRESH_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  const items = data?.items ?? [];
  const totals: Record<EmailRow["status"], number> = {
    accepted: 0,
    sent: 0,
    delivered: 0,
    bounced: 0,
    complained: 0,
    suppressed: 0,
    failed: 0,
    error: 0,
    queued: 0,
    ...(data?.totals ?? {}),
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={toEmail}
          onChange={(e) => setToEmail(e.target.value)}
          placeholder="Ontvanger (exact e-mailadres)"
          className="min-w-[260px] flex-1 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1.5 text-xs"
        >
          <option value="">Soort: alle</option>
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1.5 text-xs"
        >
          <option value="">Status: alle</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1.5 text-xs"
        >
          <option value={1}>Laatste 24u</option>
          <option value={7}>Laatste 7 dagen</option>
          <option value={30}>Laatste 30 dagen</option>
          <option value={90}>Laatste 90 dagen</option>
        </select>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
        >
          Vernieuwen
        </button>
      </div>

      {/* Totals */}
      <div className="flex flex-wrap gap-3 text-xs">
        <span
          className="rounded bg-green-100 px-2 py-1 text-green-700"
          title="Geaccepteerd door Resend (geen bezorgbevestiging)"
        >
          ✓ accepted {totals.accepted}
        </span>
        <span
          className="rounded bg-emerald-200 px-2 py-1 text-emerald-800"
          title="Bezorgd (bevestigd door Resend-webhook)"
        >
          ✓ delivered {totals.delivered}
        </span>
        <span
          className="rounded bg-red-100 px-2 py-1 text-red-700"
          title="Gebounced of als spam gemarkeerd (Resend-webhook)"
        >
          ✗ bounced {totals.bounced + totals.complained}
        </span>
        <span
          className="rounded bg-red-200 px-2 py-1 text-red-800"
          title="Onderdrukt: adres staat op de Resend-suppressielijst en krijgt niets meer tot je het daar verwijdert"
        >
          ⛔ suppressed {totals.suppressed}
        </span>
        {totals.sent > 0 ? (
          <span
            className="rounded bg-green-50 px-2 py-1 text-green-600"
            title="Oud label voor rijen vóór de herbenoeming (legacy)"
          >
            ✓ sent (legacy) {totals.sent}
          </span>
        ) : null}
        <span className="rounded bg-amber-100 px-2 py-1 text-amber-700">
          ⚠ failed {totals.failed}
        </span>
        <span className="rounded bg-red-100 px-2 py-1 text-red-700">
          ✗ error {totals.error}
        </span>
        <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
          ⧗ queued {totals.queued}
        </span>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading && !data ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">
          Geen e-mails gevonden voor de actieve filter.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                <th className="px-4 py-2">Tijdstip</th>
                <th className="px-4 py-2">Soort</th>
                <th className="px-4 py-2">Aan</th>
                <th className="px-4 py-2">Onderwerp</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Duur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {items.map((row) => (
                <EmailRowView
                  key={row.id}
                  row={row}
                  expanded={expanded === row.id}
                  onToggle={() =>
                    setExpanded(expanded === row.id ? null : row.id)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmailRowView({
  row,
  expanded,
  onToggle,
}: {
  row: EmailRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer hover:bg-[var(--cs-ivory)]"
      >
        <td className="whitespace-nowrap px-4 py-2 text-xs text-[var(--cs-ink-muted)]">
          {formatDateTime(row.created_at, { seconds: true })}
        </td>
        <td className="px-4 py-2">
          <KindBadge kind={row.kind} />
        </td>
        <td className="px-4 py-2 text-xs">{row.to_email}</td>
        <td className="px-4 py-2 text-xs">{row.subject}</td>
        <td className="px-4 py-2">
          <StatusBadge status={row.status} />
        </td>
        <td className="px-4 py-2 text-xs text-[var(--cs-ink-muted)]">
          {row.duration_ms != null ? `${row.duration_ms} ms` : "—"}
        </td>
      </tr>
      {expanded ? (
        <tr className="bg-[var(--cs-ivory)]">
          <td colSpan={6} className="px-4 py-3 text-xs">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1">
              <dt className="font-medium">From</dt>
              <dd>{row.from_email}</dd>
              <dt className="font-medium">SMTP response</dt>
              <dd className="font-mono">{row.smtp_response ?? "—"}</dd>
              <dt className="font-medium">Error</dt>
              <dd className="font-mono text-red-700">{row.error ?? "—"}</dd>
              <dt className="font-medium">Request IP</dt>
              <dd className="font-mono">{row.request_ip ?? "—"}</dd>
              <dt className="font-medium">User</dt>
              <dd className="font-mono">{row.related_user_id ?? "—"}</dd>
              <dt className="font-medium">Log id</dt>
              <dd className="font-mono">{row.id}</dd>
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}
